import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { useContent } from "@/context/ContentContext";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const { posts } = useContent();
  const post = posts.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const others = posts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <>
      <article className="container-site pb-24 pt-16 sm:pt-24" data-testid="blog-post-page">
        <Link
          to="/blog"
          data-testid="blog-post-back-link"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors duration-300 hover:text-terracotta-500"
        >
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <div className="mx-auto mt-10 max-w-3xl">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-forest/10 px-3.5 py-1.5 text-xs font-medium text-forest dark:bg-sage/15 dark:text-sage" data-testid="blog-post-category">
              {post.category}
            </span>
            {post.status && post.status !== "published" && (
              <span className="rounded-full bg-terracotta-100 px-3.5 py-1.5 text-xs font-medium text-terracotta-600 dark:bg-terracotta-500/15 dark:text-terracotta-400">
                {post.status === "scheduled" ? "Scheduled" : "Draft content"}
              </span>
            )}
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {post.readTime}
            </span>
          </div>

          <h1 className="mt-6 font-heading text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            <MaskedLine delay={0.1}>{post.title}</MaskedLine>
          </h1>
          <Reveal delay={0.25}>
            <p className="mt-4 text-sm text-muted-foreground">
              {new Date(post.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              {" · "}Evol &amp; Troppus
            </p>
          </Reveal>

          <Reveal delay={0.35}>
            <div className="mt-10 space-y-6" data-testid="blog-post-body">
              {post.body.map((block, i) =>
                block.type === "h2" ? (
                  <h2 key={i} className="pt-4 font-heading text-xl font-semibold text-foreground sm:text-2xl">
                    {block.text}
                  </h2>
                ) : (
                  <p key={i} className="text-base leading-relaxed text-foreground/85">
                    {block.text}
                  </p>
                )
              )}
            </div>
          </Reveal>
        </div>
      </article>

      <section className="border-t border-border bg-card py-20" data-testid="blog-more-section">
        <div className="container-site">
          <h2 className="font-heading text-2xl font-semibold text-foreground">Keep reading</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="card-lift group rounded-3xl border border-border bg-background p-7"
              >
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-terracotta-500">{p.category}</p>
                <h3 className="mt-3 font-heading text-lg font-semibold text-foreground transition-colors duration-300 group-hover:text-terracotta-500">
                  {p.title}
                </h3>
                <p className="mt-2 flex items-center gap-2 text-sm font-medium text-forest dark:text-sage">
                  Read article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
